import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Card, CardContent, Collapse, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection } from 'constants/coreDesignSystem';
import useSort from 'hooks/useSort';

import { ReportsTestIds } from '../reports.testIds';

import { qualifiedIncomesTableConfig, validationRulesTableConfig } from './Incomes.config';
import { COLLAPSED_BOTTOM_PADDING } from './Incomes.constants';

const defaultObject = {};

const rulesDefaultOrder = { id: 'ruleName' };
const incomesDefaultOrder = { id: 'incomeType' };

const getStyles = (theme, { isCollapsed }) => ({
  list: {
    borderRadius: '8px',
    transitionProperty: 'box-shadow',
    transitionDuration: `${theme.transitions.duration.short}ms`,
    transitionTimingFunction: theme.transitions.easing.easeInOut,
  },
  listContent: {
    '&:last-child': {
      paddingBottom: !isCollapsed ? 0 : COLLAPSED_BOTTOM_PADDING,
    },
  },
});

const Incomes = ({ incomes = defaultObject }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { validationRules, qualifiedIncomes } = incomes;
  const theme = useTheme();

  const classes = useMemo(() => getStyles(theme, { isCollapsed }), [theme, isCollapsed]);

  const { order: rulesOrder, onRequestSort: onRequestSortRules } = useSort(rulesDefaultOrder);
  const { order: incomesOrder, onRequestSort: onRequestSortIncomes } = useSort(incomesDefaultOrder);

  const validationRulesRows = useMemo(
    () =>
      _.orderBy(
        validationRules || [],
        [rule => rule[rulesOrder.id]?.toLowerCase?.()],
        [rulesOrder.direction],
      ),
    [validationRules, rulesOrder],
  );

  const qualifiedIncomesRows = useMemo(
    () =>
      _.orderBy(
        qualifiedIncomes || [],
        [income => income[incomesOrder.id]?.toLowerCase?.()],
        [incomesOrder.direction],
      ),
    [qualifiedIncomes, incomesOrder],
  );

  const onToggleSection = useCallback(e => {
    e.stopPropagation();

    setIsCollapsed(prevState => !prevState);
  }, []);

  return (
    <Card square className="w-full mb-28 max-h-full flex flex-col" sx={classes.list}>
      <Box
        className="cursor-pointer flex items-center justify-between min-h-64 px-8 relative"
        data-testid={ReportsTestIds.INCOMES_SECTION}
        onClick={onToggleSection}
      >
        <Box className="flex items-center min-w-0 px-12 w-full">
          <Typography variant="medium16">Incomes</Typography>
        </Box>

        <Box className="flex items-center">
          <ButtonWithTooltip
            dataTestId={ReportsTestIds.INCOMES_TOGGLE_BUTTON}
            icon={ArrowCheckIcon}
            iconDirection={isCollapsed ? IconDirection.UP : IconDirection.DOWN}
            size="small"
            title={isCollapsed ? 'Collapse' : 'Expand'}
            onClick={onToggleSection}
          />
        </Box>
      </Box>

      <CardContent
        className="flex flex-col flex-1 flex-auto h-full min-h-0 w-full py-0 px-16 overflow-auto"
        sx={classes.listContent}
      >
        <Collapse in={isCollapsed} timeout="auto">
          <Typography className="text-center py-20" variant="medium16">
            Validation Rules Table
          </Typography>
          <Box data-testid={ReportsTestIds.VALIDATION_RULES_TABLE_ROW_VALIDATION_RULES_TABLE}>
            <TableCDS
              config={validationRulesTableConfig}
              data={validationRulesRows}
              order={rulesOrder}
              onRequestSort={onRequestSortRules}
            />
          </Box>

          <Typography className="text-center py-20" variant="medium16">
            Qualified Incomes Table
          </Typography>
          <Box data-testid={ReportsTestIds.VALIDATION_RULES_TABLE_ROW_QUALIFIED_INCOMES_TABLE}>
            <TableCDS
              config={qualifiedIncomesTableConfig}
              data={qualifiedIncomesRows}
              order={incomesOrder}
              onRequestSort={onRequestSortIncomes}
            />
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

Incomes.propTypes = {
  incomes: PropTypes.object,
};

export default memo(Incomes);
